import { resolveEndpointPosition, resolveEndpointDirection } from '../types';
import { buildSegmentPath } from './segmentPathBuilders';
import { getHandler } from '../entities/registry';
import type { BulkDragContext } from './buildBulkDragContext';

/**
 * Imperatively updates DOM elements during drag for entities, expanded groups,
 * collapsed groups, and affected SVG edge paths.
 */
export function applyImperativeDragDom(
  ctx: BulkDragContext,
  moves: Array<{ id: string; x: number; y: number }>,
  groupMoves: Array<{ groupId: string; collapsedX: number; collapsedY: number }>,
): void {
  // Update entity DOM positions
  for (const move of moves) {
    const el = ctx.entityDomMap.get(move.id);
    if (!el) continue;

    const ent = ctx.entityMap.get(move.id);
    if (ent) {
      const handler = getHandler(ent.kind);
      const pos = handler.getDomPosition({ ...ent, x: move.x, y: move.y });
      el.style.left = pos.left + 'px';
      el.style.top = pos.top + 'px';
    }

    // Update local rect map for edge recalculation
    const rect = ctx.localRectMap.get(move.id);
    if (rect) { rect.x = move.x; rect.y = move.y; }
  }

  // Update expanded group boxes (shift by common delta)
  if (ctx.expandedGroupDom.length > 0 && moves.length > 0) {
    const commonDx = moves[0].x - ctx.entityStarts[0].startX;
    const commonDy = moves[0].y - ctx.entityStarts[0].startY;
    for (const { el, startLeft, startTop } of ctx.expandedGroupDom) {
      el.style.left = (startLeft + commonDx) + 'px';
      el.style.top = (startTop + commonDy) + 'px';
    }
  }

  // Update collapsed group DOM positions
  for (const gm of groupMoves) {
    const el = ctx.entityDomMap.get(gm.groupId);
    if (el) {
      el.style.left = gm.collapsedX + 'px';
      el.style.top = gm.collapsedY + 'px';
    }
  }

  // Update dep-explorer overlay positions
  if (ctx.depOverlayDom.size > 0) {
    const handledOverlayIds = new Set<string>();

    for (const move of moves) {
      const ov = ctx.depOverlayDom.get(move.id);
      if (!ov) continue;
      handledOverlayIds.add(move.id);
      const ent = ctx.entityMap.get(move.id);
      if (!ent) continue;
      const handler = getHandler(ent.kind);
      const pos = handler.getDomPosition({ ...ent, x: move.x, y: move.y });
      if (ov.isFocus) {
        ov.el.style.left = (pos.left - 3) + 'px';
        ov.el.style.top = (pos.top - 3) + 'px';
      } else {
        ov.el.style.left = pos.left + 'px';
        ov.el.style.top = pos.top + 'px';
      }
    }

    for (const gm of groupMoves) {
      const ov = ctx.depOverlayDom.get(gm.groupId);
      if (!ov) continue;
      handledOverlayIds.add(gm.groupId);
      if (ov.isFocus) {
        ov.el.style.left = (gm.collapsedX - 3) + 'px';
        ov.el.style.top = (gm.collapsedY - 3) + 'px';
      } else {
        ov.el.style.left = gm.collapsedX + 'px';
        ov.el.style.top = gm.collapsedY + 'px';
      }
    }

    // Expanded group overlays: shift by common delta (same as expanded group boxes)
    if (ctx.expandedGroupDom.length > 0 && moves.length > 0) {
      const commonDx = moves[0].x - ctx.entityStarts[0].startX;
      const commonDy = moves[0].y - ctx.entityStarts[0].startY;
      for (const [id, ov] of ctx.depOverlayDom) {
        if (handledOverlayIds.has(id)) continue;
        ov.el.style.left = (ov.startLeft + commonDx) + 'px';
        ov.el.style.top = (ov.startTop + commonDy) + 'px';
      }
    }
  }

  // Update dep-explorer toggle button positions
  if (ctx.depToggleDom.size > 0) {
    const handledToggleIds = new Set<string>();

    for (const move of moves) {
      const tg = ctx.depToggleDom.get(move.id);
      if (!tg) continue;
      handledToggleIds.add(move.id);
      const ent = ctx.entityMap.get(move.id);
      if (!ent) continue;
      const handler = getHandler(ent.kind);
      const pos = handler.getDomPosition({ ...ent, x: move.x, y: move.y });
      // Toggle is positioned at top-right: left = x + width - 10, top = y - 6
      tg.el.style.left = (pos.left + ent.width - 10) + 'px';
      tg.el.style.top = (pos.top - 6) + 'px';
    }

    // Expanded group toggles: shift by common delta
    if (ctx.expandedGroupDom.length > 0 && moves.length > 0) {
      const commonDx = moves[0].x - ctx.entityStarts[0].startX;
      const commonDy = moves[0].y - ctx.entityStarts[0].startY;
      for (const [id, tg] of ctx.depToggleDom) {
        if (handledToggleIds.has(id)) continue;
        tg.el.style.left = (tg.startLeft + commonDx) + 'px';
        tg.el.style.top = (tg.startTop + commonDy) + 'px';
      }
    }
  }

  // Update resource chip overlay positions
  if (ctx.resourceChipDom.size > 0) {
    const handledChipIds = new Set<string>();

    for (const move of moves) {
      const rc = ctx.resourceChipDom.get(move.id);
      if (!rc) continue;
      handledChipIds.add(move.id);
      rc.el.style.left = move.x + 'px';
      rc.el.style.top = (move.y - 18) + 'px';
    }

    for (const gm of groupMoves) {
      const rc = ctx.resourceChipDom.get(gm.groupId);
      if (!rc) continue;
      handledChipIds.add(gm.groupId);
      rc.el.style.left = gm.collapsedX + 'px';
      rc.el.style.top = (gm.collapsedY - 18) + 'px';
    }

    // Expanded group chips: shift by common delta
    if (ctx.expandedGroupDom.length > 0 && moves.length > 0) {
      const commonDx = moves[0].x - ctx.entityStarts[0].startX;
      const commonDy = moves[0].y - ctx.entityStarts[0].startY;
      for (const [id, rc] of ctx.resourceChipDom) {
        if (handledChipIds.has(id)) continue;
        rc.el.style.left = (rc.startLeft + commonDx) + 'px';
        rc.el.style.top = (rc.startTop + commonDy) + 'px';
      }
    }
  }

  // Update affected SVG edge paths
  for (const { segment, pathEls } of ctx.affectedSegments) {
    const fromPos = resolveEndpointPosition(segment.from, ctx.localRectMap);
    const toPos = resolveEndpointPosition(segment.to, ctx.localRectMap);
    if (!fromPos || !toPos) continue;
    const fromDir = resolveEndpointDirection(segment.from, fromPos, toPos);
    const toDir = resolveEndpointDirection(segment.to, toPos, fromPos);
    const newPath = buildSegmentPath(fromPos, toPos, fromDir, toDir, segment.segmentType);
    pathEls.forEach(p => p.setAttribute('d', newPath));
  }
}
